import React, { useState, useRef, useEffect, useCallback } from "react";
import { Wifi, WifiOff, RefreshCw, AlertCircle } from "lucide-react";
import io from "socket.io-client";
import ChatSidebar from "./ChatSidebar";
import MobileChat, { MobileChatSelection } from "./MobileChat"; // Import MobileChatSelection
import WarningBanner from "./WarningBanner";
import Message from "./Message";
import MessageInput from "./MessageInput";
import { envConfig } from "../../config/env";
import chatService from "../../services/chat";
import { useNavigate } from "react-router-dom";
import { getOrCreateTimer } from "./timerStore";
import { successToast, errorToast } from "../../utils/toasters";

// JWT decode function
const jwtDecode = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(""),
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error decoding JWT:", error);
    return null;
  }
};

// Network status hook
const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return isOnline;
};

const ResponsiveChatLayout = () => {
  const [userId, setUserId] = useState(null);
  // active group
  const [activeGroup, setActiveGroup] = useState(null);
  const activeGroupRef = useRef(null);
  const [allGroups, setAllGroups] = useState(null);

  async function handleJoinGroup(group) {
    try {
      if (!userId) {
        throw new Error("userid not found");
      }
      setIsLoading(true);
      const response = await chatService.addUserToGroup({
        vibe_id: group.vibe_id,
        user_id: userId,
      });
      setActiveGroup(group);
      activeGroupRef.current = group;
    } catch (error) {
      console.error("Error while trying to joina grouup : ", error);
    }
  }

  // Core state
  const navigate = useNavigate();
  const [socket, setSocket] = useState(null);
  const [username, setUsername] = useState("");
  const [isConnected, setIsConnected] = useState(false);

  // UI state
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Chat state
  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [currentRecipientId, setCurrentRecipientId] = useState(null);
  const [currentRecipientName, setCurrentRecipientName] = useState("");
  const [currentRecipientImage, setCurrentRecipientImage] = useState("");

  // Loading and error state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [isConnecting, setIsConnecting] = useState(false);

  // Refs
  const messagesEndRef = useRef(null);
  const activeRoomRef = useRef(null);

  async function handleCloseChat(chatId) {
    try {
      if (!chatId) {
        throw new Error("chatid not found");
      }
      socket.emit("close-chat", {
        roomId: chatId,
        reason: "Your Chat time with this person has ended. ",
      });
    } catch (error) {
      console.error("Error while trying to joina grouup : ", error);
    }
  }
  // Network status
  const isOnline = useNetworkStatus();

  // Server configuration
  const SERVER_URL = envConfig.chatApiUrl;
  const MAX_RECONNECT_ATTEMPTS = 10;

  useEffect(() => {
    async function fetchProfile() {
      const profile = await chatService.fetchProfile();
      setUsername(profile.profile.chat_name);
    }
    async function fetchVibesAsGroups() {
      const groups = await chatService.getGroupsWithConnectedUsers();
      setAllGroups(groups.data.data);
    }
    fetchProfile();
    fetchVibesAsGroups();
    setInterval(() => {
      fetchVibesAsGroups();
    }, 5000);
  }, []);
  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Initialize user ID from JWT token
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (token) {
      const decoded = jwtDecode(token);
      if (decoded && decoded.userid) {
        setUserId(decoded.userid);
      } else {
        setError("Invalid token format. Please login again.");
      }
    } else {
      setError("No access token found. Please login first.");
    }
  }, []);

  // Auto-scroll to bottom
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Load rooms from API
  const loadRoomsAPI = async () => {
    const token = localStorage.getItem("access_token");
    if (!token) throw new Error("No access token");

    const response = await fetch(`${SERVER_URL}/api/chat/rooms`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data;
  };

  const loadRooms = useCallback(async () => {
    if (!userId) return;

    try {
      setIsLoading(true);

      const roomsData = await loadRoomsAPI();

      // Format rooms for display
      const formattedRooms = roomsData.map((room) => ({
        id: room.room_id,
        name: room.other_user
          ? room.other_user.chat_name
          : `User ${room.user1_id === userId ? room.user2_id : room.user1_id}`,
        lastMessage: room.last_message
          ? room.last_message.message
          : "Start conversation...",
        lastMessageTime: room.last_message
          ? new Date(room.last_message.created_at).toLocaleTimeString()
          : "",
        unreadCount: room.unread_count || 0,
        otherUserId: room.user1_id === userId ? room.user2_id : room.user1_id,
        avatar: room.other_user.profile_picture,
        isOnline: true,
      }));

      setRooms(formattedRooms);
      // Do not automatically select the first chat on load in mobile view
      // if (formattedRooms && formattedRooms.length > 0) {
      //   handleChatSelect(formattedRooms[0]);
      // }
      setError("");
    } catch (error) {
      console.error("Error loading rooms:", error);
      setError(`Failed to load rooms: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  // Update room's last message in the list
  const updateRoomLastMessage = useCallback(
    (roomId, message, senderId) => {
      setRooms((prevRooms) => {
        return prevRooms.map((room) => {
          if (room.id === roomId) {
            return {
              ...room,
              lastMessage: message,
              lastMessageTime: new Date().toLocaleTimeString(),
              unreadCount:
                senderId !== userId && roomId !== activeRoomRef.current
                  ? (room.unreadCount || 0) + 1
                  : room.unreadCount || 0,
            };
          }
          return room;
        });
      });
    },
    [userId],
  );

  // Clear unread count for a room
  const clearUnreadCount = useCallback((roomId) => {
    setRooms((prevRooms) => {
      return prevRooms.map((room) => {
        if (room.id === roomId) {
          return { ...room, unreadCount: 0 };
        }
        return room;
      });
    });
  }, []);

  // Socket connection
  const connectSocket = useCallback(() => {
    if (!userId || !isOnline || username === "" || isConnecting || socket) {
      return;
    }

    const token = localStorage.getItem("access_token");
    if (!token) {
      setError("No access token found");
      return;
    }

    setIsConnecting(true);
    setConnectionStatus("connecting");

    const newSocket = io(SERVER_URL, {
      auth: { token, username },
      transports: ["polling", "websocket"],
      timeout: 10000,
      forceNew: true,
      reconnection: false,
    });

    newSocket.on("connect", () => {
      setIsConnected(true);
      setIsConnecting(false);
      setConnectionStatus("connected");
      setSocket(newSocket);
      setError("");
      setReconnectAttempts(0);
      loadRooms();
    });

    newSocket.on("disconnect", (reason) => {
      setIsConnected(false);
      setIsConnecting(false);
      setConnectionStatus("disconnected");
      setSocket(null);

      if (
        reason !== "io client disconnect" &&
        reconnectAttempts < MAX_RECONNECT_ATTEMPTS
      ) {
        setTimeout(() => {
          setReconnectAttempts((prev) => prev + 1);
          connectSocket();
        }, 8000);
      }
    });

    newSocket.on("connect_error", (error) => {
      console.error(" Connection error:", error);
      setIsConnecting(false);
      setConnectionStatus("error");
      setSocket(null);
      setError("Connection failed");
    });

    newSocket.on("chat-closed", (data) => {
      const { reason, roomId } = data;
      successToast(reason);
      setActiveRoom(null);
      if (activeRoomRef.current === roomId) {
        activeRoomRef.current = null;
        setSelectedChatId(null);
        setMessages([]);
        setRooms([]);
      }
      loadRooms();
    });
    // Message events
    newSocket.on("room_joined", async (data) => {
      setActiveRoom(data.roomId);
      activeRoomRef.current = data.roomId;
      setSelectedChatId(data.roomId);

      getOrCreateTimer(data.roomId, 10 * 60);
      const formattedMessages = data.messages.map((msg) => ({
        id: msg.message_id,
        content: msg.message,
        isUser: msg.sender_id === userId,
        timestamp: new Date(msg.created_at).toLocaleTimeString(),
        username: msg.sender_username,
      }));

      setMessages(formattedMessages);
      clearUnreadCount(data.roomId);

      await chatService.markChatAsRead(data.roomId);
    });

    newSocket.on("receive_message", (data) => {
      updateRoomLastMessage(
        data.room_id,
        data.message,
        data.sender_id,
        data.sender_username,
      );

      if (data.room_id === activeRoomRef.current) {
        const newMsg = {
          id: data.message_id,
          content: data.message,
          isUser: data.sender_id === userId,
          timestamp: new Date(data.created_at).toLocaleTimeString(),
          username: data.sender_username,
        };

        setMessages((prev) => {
          if (prev.some((msg) => msg.id === newMsg.id)) {
            return prev;
          }
          return [...prev, newMsg];
        });

        clearUnreadCount(data.room_id);
      }
    });

    newSocket.on("room_available", () => {
      loadRooms();
    });
    newSocket.on("recipient_disconnected", (data) => {
      errorToast(data.message);
      loadRooms();
      setSelectedChatId(null)
      setActiveRoom(null)
    });
    setInterval(() => {
      loadRooms();
    }, 10000);
  }, [
    userId,
    isOnline,
    isConnecting,
    socket,
    username,
    reconnectAttempts,
    loadRooms,
    updateRoomLastMessage,
    clearUnreadCount,
  ]);

  // Connect when user is available and online
  useEffect(() => {
    if (!userId || socket || isConnecting) return;

    connectSocket();
  }, [userId, username, connectSocket]);

  // Handle network status changes
  useEffect(() => {
    if (!isOnline && socket) {
      socket.disconnect();
    }
  }, [isOnline, socket]);

  // Handle chat selection
  const handleChatSelect = useCallback(
    (room) => {
      try {
        if (!socket || !isConnected) {
          throw new Error(
            "Not connected to server. Please check your connection.",
          );
        }

        setSelectedChatId(room.id);
        setActiveRoom(room.id);
        activeRoomRef.current = room.id;
        setCurrentRecipientId(room.otherUserId);
        setCurrentRecipientName(room.name);
        setCurrentRecipientImage(room.avatar);
        setMessages([]);
        clearUnreadCount(room.id);
        getOrCreateTimer(room.id, 10 * 60);

        socket.emit("join_room", { recipientId: room.otherUserId });
      } catch (error) {
        console.error("error while trying to join room: ", error);
      }
    },
    [socket, isConnected, clearUnreadCount],
  );
  const handleChatSelectForChatProfile = useCallback(
    (payload) => {
      try {
        if (!socket || !isConnected) {
          throw new Error(
            "Not connected to server. Please check your connection.",
          );
        }

        socket.emit("join_room", { recipientId: payload.userid });
        setCurrentRecipientId(payload.userid);
        // For a new chat from a profile, we don't have a room ID yet, so we set a placeholder
        // This will be updated to the actual room ID once 'room_joined' event is received.

        getOrCreateTimer(payload.id, 10 * 60);
        setCurrentRecipientName(payload.chat_name);
        setCurrentRecipientImage(payload.avatar);
        setMessages([]); // Clear messages when starting a new chat
      } catch (error) {
        console.error("error while trying to join room: ", error);
      }
    },
    [socket, isConnected],
  );

  // Send message
  const handleSendMessage = useCallback(
    (content) => {
      if (!content.trim() || !activeRoom || !socket || !isConnected) {
        setError("Cannot send message. Check your connection and try again.");
        return;
      }

      if (content.length > 1000) {
        setError("Message too long (max 1000 characters)");
        return;
      }

      const messageData = {
        roomId: activeRoom,
        message: content,
        recipientId: currentRecipientId,
      };

      socket.emit("send_message", messageData);
    },
    [activeRoom, socket, isConnected, currentRecipientId],
  );

  // Manual reconnection
  const manualReconnect = useCallback(() => {
    if (socket) {
      socket.disconnect();
    }
    setSocket(null);
    setIsConnected(false);
    setIsConnecting(false);
    setReconnectAttempts(0);
    setError("");

    setTimeout(() => {
      connectSocket();
    }, 1000);
  }, [socket, connectSocket]);

  // Connection status indicator
  const getConnectionStatus = () => {
    if (!isOnline) {
      return {
        icon: WifiOff,
        color: "text-red-500",
        text: "Offline",
        bg: "bg-red-100",
      };
    } else if (connectionStatus === "connected") {
      return {
        icon: Wifi,
        color: "text-green-500",
        text: "Connected",
        bg: "bg-green-100",
      };
    } else if (connectionStatus === "connecting") {
      return {
        icon: RefreshCw,
        color: "text-yellow-500",
        text: "Connecting",
        bg: "bg-yellow-100",
      };
    } else {
      return {
        icon: AlertCircle,
        color: "text-red-500",
        text: "Disconnected",
        bg: "bg-red-100",
      };
    }
  };

  const connectionInfo = getConnectionStatus();

  // Mobile view - switch between chat selection and active chat
  if (isMobile) {
    return (
      <div className="h-screen">
        {selectedChatId && activeRoom ? (
          <MobileChat
            messages={messages}
            onSendMessage={handleSendMessage}
            isConnected={isConnected}
            activeRoomRef={activeRoomRef}
            handleCloseChat={handleCloseChat}
            recipientName={currentRecipientName}
            recipientAvatar={currentRecipientImage}
            connectionStatus={connectionStatus}
            connectionInfo={connectionInfo}
            onReconnect={manualReconnect}
            onClose={() => {
              setSelectedChatId(null);
              setActiveRoom(null);
              setMessages([]);
            }} // Allows going back to chat list
          />
        ) : (
          <MobileChatSelection
            allGroups={allGroups}
            rooms={rooms}
            onGroupSelect={handleJoinGroup}
            onRoomSelect={handleChatSelect}
            onChatSelect={handleChatSelectForChatProfile}
            selectedGroup={activeGroup}
            isLoading={isLoading}
            error={error}
            isConnected={isConnected}
            connectionStatus={connectionStatus}
            connectionInfo={connectionInfo}
            onReconnect={manualReconnect}
          />
        )}
      </div>
    );
  }

  // Desktop view
  return (
    <div className="flex h-screen bg-gray-50">
      {/* Connection Status Bar */}
      {(connectionStatus !== "connected" || !isOnline) && (
        <div
          className={`fixed top-0 left-0 right-0 z-50 ${
            connectionInfo.bg
          } border-b px-4 py-2`}
        >
          <div className="flex items-center justify-between max-w-6xl mx-auto">
            <div className="flex items-center gap-2">
              <connectionInfo.icon
                size={16}
                className={`${connectionInfo.color} ${
                  connectionStatus === "connecting" ? "animate-spin" : ""
                }`}
              />
              <span className={`text-sm font-medium ${connectionInfo.color}`}>
                {connectionInfo.text}
                {reconnectAttempts > 0 &&
                  ` (Attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`}
              </span>
            </div>
            {connectionStatus !== "connected" &&
              isOnline &&
              reconnectAttempts > 0 && (
                <button
                  onClick={manualReconnect}
                  className="text-sm px-3 py-1 bg-white rounded border hover:bg-gray-50"
                >
                  Retry Connection
                </button>
              )}
          </div>
        </div>
      )}

      {/* Sidebar - always visible on desktop */}
      <div
        className="block flex-shrink-0"
        style={{
          marginTop:
            connectionStatus !== "connected" || !isOnline ? "48px" : "0",
        }}
      >
        <ChatSidebar
          allGroups={allGroups}
          userName={username}
          handleGroupClick={handleJoinGroup}
          activeGroup={activeGroup}
          setActiveGroup={setActiveGroup}
          onRoomSelect={handleChatSelect}
          onChatSelect={handleChatSelectForChatProfile}
          selectedChatId={selectedChatId}
          rooms={rooms}
          isLoading={isLoading}
          error={error}
          isConnected={isConnected}
        />
      </div>

      {/* Chat Area */}
      <div
        className="flex-1 flex flex-col"
        style={{
          marginTop:
            connectionStatus !== "connected" || !isOnline ? "48px" : "0",
        }}
      >
        {selectedChatId && activeRoom ? (
          <div className="flex flex-col h-full bg-white">
            <div className="relative">
              <div className="flex items-center justify-between p-4 bg-white border-b border-gray-200">
                <div
                  onClick={() => {
                    activeRoomRef.current = null;
                    navigate("/chatdetail?name=" + currentRecipientName);
                  }}
                  className="flex cursor-pointer items-center space-x-3"
                >
                  <div className="relative">
                    {currentRecipientImage && (
                      <img
                        src={currentRecipientImage}
                        alt={currentRecipientName}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    )}
                    <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {currentRecipientName}
                    </h3>
                    <p className="text-sm text-green-500">● Active</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 relative">
                  <span className="text-gray-600 font-medium">
                    {new Date().toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
              </div>
            </div>

            {/*
            <WarningBanner message="This chat stays private and smooth until you choose to reveal your identity. It will end in 10 minutes." />
            */}
            {userId && activeRoomRef.current && (
              <WarningBanner
                closeChat={handleCloseChat}
                chatId={`${activeRoomRef.current}`}
                message="This chat stays private and smooth And It will end in 10 minutes."
              />
            )}

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => (
                <Message
                  key={message.id}
                  content={message.content}
                  isUser={message.isUser}
                  timestamp={message.timestamp}
                  avatar={currentRecipientImage}
                  username={message.username}
                />
              ))}
              <div ref={messagesEndRef} />
            </div>

            <MessageInput
              onSendMessage={handleSendMessage}
              disabled={!isConnected}
              placeholder={isConnected ? "Type a message..." : "Connecting..."}
            />
          </div>
        ) : (
          <div className="flex flex-1 items-center justify-center">
            <div className="text-center text-gray-500">
              <h2 className="text-xl font-semibold mb-2">
                Select a chat to start messaging
              </h2>
              <p>Choose a conversation from the sidebar to begin</p>
              {!isConnected && (
                <p className="text-red-500 text-sm mt-2">
                  Not connected to server
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/*
      <FriendRequestModal
        isOpen={isFriendRequestModalOpen}
        onClose={() => setIsFriendRequestModalOpen(false)}
      />
      */}
    </div>
  );
};

export default ResponsiveChatLayout;
